<template>
  <span class="inquiry-notification-anchor" aria-hidden="true" />
</template>

<script setup>
import { h, onBeforeUnmount, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { ElNotification } from 'element-plus'
import SockJS from 'sockjs-client'
import * as Stomp from 'stompjs'
import { getToken } from '@/utils/auth'
import { getInquiryNotificationCandidates } from '@/api/kefu'
import { createInquiryNotificationTracker, isNewInquiryNotification } from '@/utils/inquiryNotification.mjs'
import useBusinessTodoStore from '@/store/modules/businessTodo'
import { websocketUrls } from '@/utils/websocket'

const router = useRouter()
const todoStore = useBusinessTodoStore()
const connections = new Map()
const reconnectTimers = new Map()
const notificationTracker = createInquiryNotificationTracker()
const activeNotifications = new Set()

let loginCheckTimer = null
let todoPollTimer = null
let todoBaseline = null
let audioContext = null
let activeSession = ''
let pollingSession = ''
let disposed = false

const isLoggedIn = () => !disposed && Boolean(getToken() && sessionStorage.getItem('userId'))

const currentUserId = () => sessionStorage.getItem('userId')
const currentSession = () => isLoggedIn() ? `${getToken()}\u0000${currentUserId()}` : ''

const notificationPath = notification => {
  const inquiryId = notification.inquiryId
  const buildPricingDetailPath = () => {
    if (!inquiryId) return ''
    const rawTargetPath = String(notification.targetPath || '').split('?')[0]
    const directInquiry = notification.inquirySource === 'DIRECT'
      || rawTargetPath === '/pricing/direct-inquiry'
    const encodedInquiryId = encodeURIComponent(inquiryId)
    const returnTo = directInquiry
      ? `/pricing/direct-inquiry?id=${encodedInquiryId}`
      : `/pricing/online-pricing?status=40&id=${encodedInquiryId}`
    return `/pc-operation/quotation/djredit?id=${encodedInquiryId}&returnTo=${encodeURIComponent(returnTo)}`
  }
  const appendQueryParam = (path, key, value) => {
    if (!value || new RegExp(`(?:\\?|&)${key}=`).test(path)) return path
    return `${path}${path.includes('?') ? '&' : '?'}${key}=${encodeURIComponent(value)}`
  }
  const appendInquiryContext = path => {
    let targetPath = String(path || '').trim()
    if (!targetPath) return targetPath
    targetPath = appendQueryParam(targetPath, 'id', inquiryId)
    if (notification.notificationKind === 'NEW_PRICING'
        && targetPath.split('?')[0] === '/pricing/online-pricing') {
      targetPath = appendQueryParam(targetPath, 'status', '40')
    }
    return targetPath
  }
  if (isNewInquiryNotification(notification) && inquiryId) {
    return `/kefu/shedit?id=${encodeURIComponent(inquiryId)}`
  }
  if (notification.notificationKind === 'NEW_PRICING' && inquiryId) {
    return buildPricingDetailPath()
  }
  if (notification.targetPath) return appendInquiryContext(notification.targetPath)
  if (isNewInquiryNotification(notification)) {
    return appendInquiryContext('/kefu/shlbindex')
  }
  if (notification.notificationKind === 'NEW_PRICING') {
    const path = notification.inquirySource === 'DIRECT'
      ? '/pricing/direct-inquiry'
      : '/pricing/online-pricing'
    return appendInquiryContext(path)
  }
  if (notification.notificationKind === 'INQUIRY_REMINDER') {
    return inquiryId
      ? `/supplier/quotation?id=${encodeURIComponent(inquiryId)}`
      : '/supplier/quotation'
  }
  return inquiryId
    ? `/supplier/quotation?id=${encodeURIComponent(inquiryId)}`
    : '/'
}

const unlockAudio = () => {
  const AudioContext = window.AudioContext || window.webkitAudioContext
  if (!AudioContext) return
  if (!audioContext) audioContext = new AudioContext()
  if (audioContext.state === 'suspended') audioContext.resume().catch(() => {})
}

const playAlertSound = () => {
  try {
    unlockAudio()
    if (!audioContext || audioContext.state !== 'running') return
    const now = audioContext.currentTime
    ;[0, 0.18].forEach((delay, index) => {
      const oscillator = audioContext.createOscillator()
      const gain = audioContext.createGain()
      oscillator.type = 'sine'
      oscillator.frequency.value = index === 0 ? 660 : 880
      gain.gain.setValueAtTime(0.0001, now + delay)
      gain.gain.exponentialRampToValueAtTime(0.18, now + delay + 0.02)
      gain.gain.exponentialRampToValueAtTime(0.0001, now + delay + 0.16)
      oscillator.connect(gain)
      gain.connect(audioContext.destination)
      oscillator.start(now + delay)
      oscillator.stop(now + delay + 0.17)
    })
  } catch (error) {
    console.warn('业务提醒音播放失败:', error)
  }
}

const showBusinessNotification = notification => {
  const session = currentSession()
  const targetPath = notificationPath(notification)
  const targetHref = router.resolve(targetPath).href
  const kind = notification.notificationKind || 'INQUIRY_REMINDER'
  const content = notification.content || '有新的业务待处理'
  let notificationInstance
  const navigateToBusiness = () => {
    notificationInstance?.close()
    // 登录状态检查有间隔；切换账号后不能继续点击上一位客服的残留提醒。
    if (!session || session !== currentSession()) return
    router.push(targetPath).catch(error => {
      console.warn('业务提醒页面跳转失败:', error)
    })
  }
  notificationInstance = ElNotification({
    title: notification.title || (kind === 'NEW_PRICING' ? '收到新定价' : '收到新询单'),
    message: h('div', { class: 'business-notification__message' }, [
      h('span', content),
      h('a', {
        href: targetHref,
        class: 'business-notification__action',
        onClick: event => {
          event.preventDefault()
          event.stopPropagation()
          navigateToBusiness()
        }
      }, '点击查看')
    ]),
    type: ['NEW_PRICING', 'AUTO_CLAIM_INQUIRY'].includes(kind) ? 'success' : 'warning',
    duration: 8000,
    position: 'top-right',
    customClass: 'business-notification--clickable',
    onClick: navigateToBusiness,
    onClose: () => activeNotifications.delete(notificationInstance)
  })
  activeNotifications.add(notificationInstance)
  playAlertSound()
}

const refreshTodoBaseline = () => {
  const session = currentSession()
  todoStore.refresh(true).then(counts => {
    if (session && session === currentSession()) todoBaseline = { ...counts }
  })
}

const handleMessage = message => {
  if (!isLoggedIn()) return
  let notification
  try {
    notification = JSON.parse(message.body)
  } catch (error) {
    console.warn('业务提醒消息格式错误:', error)
    return
  }

  // 切换后的客服审核及定价旧链接不再作为业务入口，当前事项由商务待办查询。
  if (["NEW_INQUIRY", "AUTO_CLAIM_INQUIRY", "NEW_PRICING"].includes(notification.notificationKind)) return
  if (!notificationTracker.accept(notification, currentUserId())) return

  showBusinessNotification(notification)
  refreshTodoBaseline()
}

const scheduleReconnect = url => {
  if (!isLoggedIn() || reconnectTimers.has(url)) return
  reconnectTimers.set(url, window.setTimeout(() => {
    reconnectTimers.delete(url)
    connectEndpoint(url)
  }, 15000))
}

const connectEndpoint = url => {
  if (!isLoggedIn() || connections.has(url)) return
  const session = currentSession()
  try {
    const socket = new SockJS(url)
    const client = Stomp.over(socket)
    connections.set(url, client)
    client.debug = () => {}
    client.heartbeat.outgoing = 20000
    client.heartbeat.incoming = 20000
    client.connect({}, () => {
      if (!isLoggedIn() || session !== currentSession()) {
        client.disconnect(() => {})
        return
      }
      client.subscribe('/topic/inquiry', message => {
        if (session === currentSession()) handleMessage(message)
      })
    }, error => {
      if (session !== currentSession()) return
      console.warn(`业务提醒连接失败(${url}):`, error)
      connections.delete(url)
      scheduleReconnect(url)
    })
  } catch (error) {
    connections.delete(url)
    console.warn(`业务提醒初始化失败(${url}):`, error)
    scheduleReconnect(url)
  }
}

const connectAll = () => websocketUrls.forEach(connectEndpoint)

const disconnectAll = () => {
  reconnectTimers.forEach(timer => window.clearTimeout(timer))
  reconnectTimers.clear()
  connections.forEach(client => {
    try {
      client.disconnect(() => {})
    } catch (error) {
      console.warn('业务提醒断开失败:', error)
    }
  })
  connections.clear()
}

const pollTodoCounts = async () => {
  if (!isLoggedIn()) return
  const session = currentSession()
  if (pollingSession === session) return
  pollingSession = session
  try {
    const counts = await todoStore.refresh(true)
    if (session !== currentSession()) return
    if (Object.prototype.hasOwnProperty.call(counts, 'customerServiceAudit')) {
      try {
        const response = await getInquiryNotificationCandidates()
        if (session !== currentSession()) return
        for (const notification of notificationTracker.observeCandidates(response.data, currentUserId())) {
          if (notificationTracker.accept(notification, currentUserId())) showBusinessNotification(notification)
        }
      } catch (error) {
        // 失败时不清空基线；下次成功查询仍可识别新的可处理询价。
        console.warn('获取客服询价提醒失败:', error?.message || '请求失败')
      }
    }
    if (!todoBaseline) {
      todoBaseline = { ...counts }
      return
    }

    const alerts = [
      {key:'businessHandoffs',kind:'BUSINESS_TASK',title:'客户业务交接通知',path:'/business-work/inquiries?page=inquiries'},
      {key:'businessInquiries',kind:'BUSINESS_TASK',title:'商务询单待办更新',path:'/business-work/inquiries?page=inquiries'},
      {key:'businessOrders',kind:'BUSINESS_TASK',title:'订单待商务处理',path:'/business-work/orders?page=orders'},
      {key:'businessLegacy',kind:'BUSINESS_TASK',title:'历史待办已移交',path:'/business-work/inquiries?page=inquiries'},
      {key:'bindingExceptions',kind:'BUSINESS_TASK',title:'客户业务归属异常，请核查交接',path:router.getRoutes().find(route=>route.path.endsWith('/shipper-management'))?.path || '/'}
    ]
    for (const alert of alerts) {
      const previous = Number(todoBaseline[alert.key] || 0)
      const current = Number(counts[alert.key] || 0)
      if (current > previous) {
        showBusinessNotification({
          notificationKind: alert.kind,
          title: alert.title,
          content: `新增 ${current - previous} 个待处理任务`,
          targetPath: alert.path
        })
      }
    }
    todoBaseline = { ...counts }
  } finally {
    if (pollingSession === session) pollingSession = ''
  }
}

const checkLoginState = () => {
  const session = currentSession()
  if (session !== activeSession) {
    disconnectAll()
    activeNotifications.forEach(notification => notification.close())
    activeNotifications.clear()
    notificationTracker.reset()
    todoBaseline = null
    activeSession = session
    if (todoPollTimer) { window.clearInterval(todoPollTimer); todoPollTimer = null }
  }
  if (isLoggedIn()) {
    if (connections.size === 0) connectAll()
    if (!todoPollTimer) {
      pollTodoCounts()
      todoPollTimer = window.setInterval(pollTodoCounts, 15000)
    }
    return
  }
  disconnectAll()
  todoBaseline = null
  if (todoPollTimer) {
    window.clearInterval(todoPollTimer)
    todoPollTimer = null
  }
}

onMounted(() => {
  window.addEventListener('pointerdown', unlockAudio, { once: true })
  window.addEventListener('keydown', unlockAudio, { once: true })
  checkLoginState()
  loginCheckTimer = window.setInterval(checkLoginState, 5000)
})

onBeforeUnmount(() => {
  disposed = true
  activeNotifications.forEach(notification => notification.close())
  activeNotifications.clear()
  disconnectAll()
  if (loginCheckTimer) window.clearInterval(loginCheckTimer)
  if (todoPollTimer) window.clearInterval(todoPollTimer)
  window.removeEventListener('pointerdown', unlockAudio)
  window.removeEventListener('keydown', unlockAudio)
  if (audioContext) audioContext.close().catch(() => {})
})
</script>

<style scoped>
.inquiry-notification-anchor {
  display: none;
}

:global(.business-notification--clickable) {
  cursor: pointer;
}

:global(.business-notification__message) {
  display: flex;
  align-items: center;
  gap: 10px;
}

:global(.business-notification__message > span) {
  min-width: 0;
  flex: 1;
}

:global(.business-notification__action) {
  flex: none;
  padding: 0;
  border: 0;
  background: transparent;
  color: var(--el-color-primary);
  cursor: pointer;
  font: inherit;
  font-weight: 600;
  text-decoration: none;
}
</style>
